/**
 * @file list-vendors.tsx
 * @deprecated Copied to app/modules/procurement/pages/vendors_management/list-vendors.tsx. This file still serves the existing /UProcure routes.
 * @module app/modules/uProcure/vendors_management/list-vendors
 * @description Page component for managing vendors in the vendors management section.
 *   Displays a list of vendors with pagination, search functionality, and CRUD operations.
 *   Allows users to view, edit, and delete vendors with warehouse context integration.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 2.1.2
 *
 * @todo Route-level permission: no hasModulePermission(AllVendors) on mount (only Add on
 *   add-vendor route); align with other uProcure modules or uprocure-admin pattern.
 * @todo Breadcrumb paths in index.tsx use /vendors instead of /UProcure/vendors — fix in
 *   vendors_management/index.tsx vendorBreadCrumbs and payment breadcrumbs.
 * @todo Duplicate titles: index.tsx PageTitle vs any in-component h1 on child routes.
 * @todo Assign-products navigation (navigateTo assign-product-to-vendors with state.id) —
 *   verify end-to-end with migrated assign-product-to-vendors module.
 * @todo vendor-profile/Catalogue/catalogue.tsx fetchProducts API is fully commented out —
 *   vendor catalogue tab shows no products until seller.listSellerProduct is wired.
 */

import React, { useState, useEffect } from "react";
import DynamicListLayout from "../../../custom/layouts/DynamicListLayout";
import { CustomTable } from "../../../custom/data-table";
import { Link, useNavigate } from "react-router-dom";
import { ColumnDef } from "@tanstack/react-table";
import { CustomSearchInput } from "../../../custom/search";
import {
  Accepted,
  AllSeller,
  AllVendors,
  InfoRequired,
  NewRequests,
  PAGE_LIMIT,
  Rejected,
  UnderReview,
  Add,
  PermissionModule,
  View,
  Edit,
  Delete,
  AllPermissions,
} from "../../../../utils/constants";
import { CustomToggleSwitch } from "../../../custom/toggle-switch/CustomToggleSwitch";
import APICallService from "../../../../api/apiCallService";
import { vendor } from "../../../../api/apiEndPoints";
import { getKey, removeKey, setKey } from "../../../../Global/history";
import { listVendor, listVendors } from "../../../../utils/storeString";
import { useDebounce } from "../../../../utils/useDebounce";
import Method from "../../../../utils/methods";
import CustomDatePicker from "../../../custom/DateRange/CustomDatePicker";
import { ElementAnimateUtil } from "../../../../_admin/assets/ts/_utils";
import { useNavigationHistory } from "../../../../utils/useNavigationHistory";
import { Button } from "react-bootstrap";
import { useAuth } from "../../auth";

/* ----------------------------------------------
   TYPES
---------------------------------------------- */
export interface IKYCInfo {
  status: number;
  statusMessage?: string;
  profileUpdatedAt?: string;
  statusUpdatedAt?: string;
}

export interface IGoodsLoadingArea {
  name: string;
  reference: string;
}

export interface IVendorList {
  id?: number;
  _id?: string;
  name: string;
  contactPerson?: string;
  email?: string;
  phone?: string;
  phoneCountry?: string;
  address?: string;
  image?: string;
  deactivated?: boolean;
  accountStatus?: "Active" | "Inactive";
  _createdAt?: string;
  createdAt?: string;
  KYC?: IKYCInfo | null;
}

/* ----------------------------------------------
   MAIN COMPONENT
---------------------------------------------- */
const VendorsList: React.FC = () => {
  const {navigateTo} = useNavigationHistory();
  const { currentUser } = useAuth();

  const [activeTab, setActiveTab] = useState<number>(() => {
    const savedTab = getKey(listVendor.currentTab);
    return savedTab ? parseInt(savedTab as string, 10) : 1;
  });

  const [search, setSearch] = useState<string | any>(() => {
    const savedSearch = getKey(listVendor.search);
    return savedSearch ? savedSearch : "";
  });

  const [vendors, setVendors] = useState<IVendorList[]>([]);

  const [page, setPage] = useState<number>(() => {
    const savedPage = getKey(listVendor.page);
    return savedPage ? parseInt(savedPage as string, 10) : 1;
  });

  const [pageLimit, setPageLimit] = useState<number>(() => {
    const savedLimit = getKey(listVendor.limit);
    return savedLimit ? parseInt(savedLimit as string, 10) : PAGE_LIMIT;
  });

  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);

  const [startDate, setStartDate] = useState<any>(() => {
    const savedDate = getKey(listVendor.filterStartDate);
    return savedDate ? new Date(savedDate.toString()) : null;
  });
  const [endDate, setEndDate] = useState<any>(() => {
    const savedDate = getKey(listVendor.filterEndDate);
    return savedDate ? new Date(savedDate.toString()) : null;
  });
  const [dataFetched, setDataFetched] = useState(false);

  /* ----------------------------------------------
     TAB → STATUS MAP
  ---------------------------------------------- */
  const getSellerStatusByTab = (tab: number): number[] => {
    switch (tab) {
      case 2:
        return [NewRequests - 1, UnderReview - 1, InfoRequired - 1];
      case 3:
        return [Accepted - 1];
      case 4:
        return [Rejected - 1];
      default:
        return [];
    }
  };

  /* ----------------------------------------------
     API CALL (REFERENCE FROM fetchSellerData)
  ---------------------------------------------- */
  const fetchVendorsList = async (
    pageNo: number,
    limit: number,
    searchValue: string,
    tab: number,
    fromDate?: string, // New param
    toDate?: string // New param
  ) => {
    try {
      setLoading(true);

      let params: any = {
        sortKey: "_createdAt",
        sortOrder: -1,
        needCount: true,
        skip: (pageNo - 1) * limit,
        limit: limit,
      };

      if (searchValue?.trim()) {
        params.searchTerm = searchValue;
      }

      if (fromDate) {
        params.fromDate = fromDate;
      }
      if (toDate) {
        params.toDate = toDate;
      }

      const statusArr = getSellerStatusByTab(tab);
      if (statusArr.length) {
        statusArr.forEach((val, index) => {
          params[`status[${index}]`] = val;
        });
      }

      const apiService = new APICallService(
        vendor.listVendor,
        params,
        "",
        "",
        "",
        "",
        AllSeller
      );

      const response = await apiService.callAPI();

      if (response) {
        setVendors(response?.records || []);
        setTotalRecords(response?.total || 0);
      }
    } catch (error) {
      console.error("Vendor list API failed", error);
    } finally {
      setDataFetched(true);
      setLoading(false);
    }
  };

  /* ----------------------------------------------
     EFFECT
  ---------------------------------------------- */
  useEffect(() => {
    fetchVendorsList(
      page,
      pageLimit,
      search,
      activeTab,
      startDate?.toISOString(),
      endDate?.toISOString()
    );
  }, []);

  useEffect(() => {
    if (dataFetched) {
      let initialScrollPosition: any = getKey(listVendor.scrollPosition);
      if (initialScrollPosition) {
        ElementAnimateUtil.scrollTo(parseInt(initialScrollPosition));
        setDataFetched(false);
      }
    }
  }, [dataFetched]);

  /* ----------------------------------------------
     HANDLERS
  ---------------------------------------------- */

  const handleTabChange = async (val: string | number) => {
    const numericValue = typeof val === "string" ? parseInt(val) : val;
    setActiveTab(numericValue);
    setPage(1);
    setKey(listVendor.currentTab, numericValue);
    setKey(listVendor.page, 1);
    await fetchVendorsList(
      1,
      pageLimit,
      search,
      numericValue,
      startDate?.toISOString(),
      endDate?.toISOString()
    );
  };

  const debounce = useDebounce(fetchVendorsList, 300);

  const handleSearch = async (input: string | number | any) => {
    setSearch(input);
    setPage(1);
    setKey(listVendor.page, 1);
    setKey(listVendor.search, input, false);

    await debounce(1, pageLimit, input, activeTab, startDate?.toISOString(), endDate?.toISOString());
  };

  const handleDateFilter = async (event: any) => {
  const start = event?.[0];
  const end = event?.[1];

  setStartDate(start);
  setEndDate(end);
  setPage(1);
  setKey(listVendor.page, 1);

  // When both dates selected
  if (start && end) {
    const startDateFormatted = Method.convertDateToFormat(start, "YYYY-MM-DD");
    const endDateFormatted = Method.convertDateToFormat(end, "YYYY-MM-DD");

    setKey(listVendor.filterStartDate, start, false);
    setKey(listVendor.filterEndDate, end, false);

    await fetchVendorsList(
      1,
      pageLimit,
      search,
      activeTab,
      startDateFormatted,
      endDateFormatted
    );
  }

  // When datepicker is cleared
  if (!start && !end) {
    removeKey(listVendor.filterStartDate);
    removeKey(listVendor.filterEndDate);

    await fetchVendorsList(
      1,
      pageLimit,
      search,
      activeTab,
      "",
      ""
    );
  }
};

  const handleCurrentPage = async (newPage: number) => {
    if (newPage === page || newPage.toString() === "...") return;
    setPage(newPage);
    setKey(listVendor.page, newPage);
    await fetchVendorsList(
      newPage,
      pageLimit,
      search,
      activeTab,
      startDate?.toISOString(),
      endDate?.toISOString()
    );
  };

  const handleNextPage = async (newPage: number) => {
    setPage(newPage + 1);
    setKey(listVendor.page, newPage + 1);
    await fetchVendorsList(
      newPage + 1,
      pageLimit,
      search,
      activeTab,
      startDate?.toISOString(),
      endDate?.toISOString()
    );
  };

  const handlePreviousPage = async (newPage: number) => {
    setPage(newPage - 1);
    setKey(listVendor.page, newPage - 1);
    await fetchVendorsList(
      newPage - 1,
      pageLimit,
      search,
      activeTab,
      startDate?.toISOString(),
      endDate?.toISOString()
    );
  };

  const handlePageLimit = async (event: any) => {
    const newLimit = parseInt(event.target.value);
    setPageLimit(newLimit);
    setPage(1);
    setKey(listVendor.limit, newLimit);
    setKey(listVendor.page, 1);
    await fetchVendorsList(
      1,
      newLimit,
      search,
      activeTab,
      startDate?.toISOString(),
      endDate?.toISOString()
    );
  };

  /* ----------------------------------------------
     TOGGLE STATUS
  ---------------------------------------------- */
  // const handleVendorStatus = (id: string | undefined, value: boolean) => {
  //   setVendors((prev) =>
  //     prev.map((v) =>
  //       v._id === id
  //         ? { ...v, accountStatus: value ? "Active" : "Inactive" }
  //         : v
  //     )
  //   );
  // };

  /* ----------------------------------------------
     TABLE COLUMNS
  ---------------------------------------------- */
  const columns: ColumnDef<IVendorList>[] = [
    {
      header: "Supplier Name",
      accessorKey: "ownerName",
      size: 150,
    },
    {
      header: "Email",
      accessorKey: "email",
      size: 150,
    },
    {
      header: "Phone",
      size: 180,
      cell: ({ row }) => {
        return (
          <span>{row.original.phoneCountry + " " + row.original.phone}</span>
        );
      },
    },
    {
      header: "Address",
      accessorKey: "address.addressLine1",
      size: 200,
    },
    {
      header: "Verification Status",
      size: 140,
      cell: ({ row }) => {
        const status = row.original.KYC?.status;

        switch (status) {
          case Accepted - 1:
            return <span className="badge fs-14 badge-light-success">Approved</span>;
          case Rejected - 1:
            return <span className="badge fs-14 badge-light-danger">Rejected</span>;
          case InfoRequired - 1:
            return (
              <span className="badge fs-14 badge-light-warning">Info Required</span>
            );
          default:
            return (
              <span className="badge fs-14 badge-light-warning">Under Review</span>
            );
        }
      },
    },
    ...([1, 3].includes(activeTab)
      ? [
          {
            header: "Action",
            size: 200,
            cell: ({ row }: { row: { original: IVendorList } }) => {
              const status = row.original.KYC?.status;
              return (
                <div
                  style={{
                    display: "flex",
                    gap: "8px",
                    flexWrap: "wrap",
                    alignItems: "center",
                  }}
                >
                  <Button className="uw-btn uw-btn-primary uw-btn-sm"
                    size="sm"
                    variant="primary"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAssignedProducts(row?.original?._id ?? "");
                    }}
                    style={{
                      whiteSpace: "nowrap",
                      fontSize: "12px",
                      padding: "6px 12px",
                      width: "100%",
                    }}
                    disabled={status !== 4}
                  >
                    Assigned Products
                  </Button>
                  {/* <Button
                    size="sm"
                    variant="primary"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleVendorProfile(row?.original?._id);
                    }}
                    style={{
                      whiteSpace: "nowrap",
                      fontSize: "12px",
                      padding: "6px 12px",
                      width: "100%",
                    }}
                  >
                    View Details
                  </Button> */}
                </div>
              );
            },
          } as ColumnDef<IVendorList>,
        ]
      : []),
  ];

  const handleVendorProfile = (id: any) => {
    let currentScrollY = window.scrollY;
    setKey(listVendor.scrollPosition, currentScrollY);
    // Only save search if it has a valid value
    if (search && search.trim()) {
      setKey(listVendor.search, search, false);
    } else {
      setKey(listVendor.search, "", false);
    }
    setKey(listVendor.filterStartDate, startDate, false);
    setKey(listVendor.filterEndDate, endDate, false);
    navigateTo("/UProcure/vendors/vendor-profile", {
      state: { id: id, moduleName: AllVendors },
    });
  };

  const handleAssignedProducts = (id: string) => {
    let currentScrollY = window.scrollY;
    setKey(listVendor.scrollPosition, currentScrollY);
    setKey(listVendor.scrollPosition, currentScrollY);
    setKey(listVendor.search, search, false);
    setKey(listVendor.filterStartDate, startDate, false);
    setKey(listVendor.filterEndDate, endDate, false);
    navigateTo("/UProcure/assign-product-to-vendors", {
      state: { id: id },
    });
  };

  /* ----------------------------------------------
     TABS
  ---------------------------------------------- */
  const tabs = [
    { label: "All Suppliers", value: 1 },
    { label: "Pending Approval", value: 2 },
    { label: "Approved", value: 3 },
    { label: "Rejected", value: 4 },
  ];

  return (
    <DynamicListLayout
      title="Suppliers"
      variant="withTabs"
      activeTab={activeTab}
      onTabChange={handleTabChange}
      tabs={tabs}
      page={page}
      pageLimit={pageLimit}
      totalRecords={totalRecords}
      handleCurrentPage={handleCurrentPage}
      handleNextPage={handleNextPage}
      handlePreviousPage={handlePreviousPage}
      handlePageLimit={handlePageLimit}
      action={
        Method.hasPermission(PermissionModule.AllVendors, Add, currentUser) ? (
          <Link to="/UProcure/vendors/add-new-vendor">
            <Button className="uw-btn uw-btn-primary uw-btn-lg" variant="primary" size="lg">
              Add New Supplier
            </Button>
          </Link>
        ) : null
      }
      filters={
        <div className="d-flex gap-4 align-items-center flex-wrap">
          <div className="min-w-280px max-w-280px">
            <CustomSearchInput
              placeholder="Search supplier"
              defaultValue={search}
              onSearch={handleSearch}
            />
          </div>
          <div className="min-w-280px max-w-280px">
            <CustomDatePicker
              onChange={handleDateFilter}
              selectsRange={true}
              startDate={startDate}
              endDate={endDate}
              dateFormat="dd/MM/yyyy"
              showFullMonthYearPicker={true}
              placeholderText="Select dates"
              maxDate={new Date()}
              isClearable={true}
              inputTextBG="bg-white"
            />
          </div>
        </div>
      }
    >
      <CustomTable
        data={vendors}
        columns={columns}
        loading={loading}
        onRowClick={(vendor: IVendorList) => {
          handleVendorProfile(vendor._id);
        }}
      />
    </DynamicListLayout>
  );
};

export default VendorsList;
